import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { API_BASE_URL } from '../config/api';
import { dollarsToCents, formatCents } from '../utils/money';

/** Customer-scoped remote selection. Search and every page use server balances. */
export function PayableSelect({ apiCall, customerId, kind = 'transaction', selectedId, onSelect,
  excludeIds = [], reconciliationId, required = true }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState({ data: [], next_cursor: null });
  const [cursors, setCursors] = useState([null]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const [selected, setSelected] = useState(null);
  const sequence = useRef(0);
  const previousSelection = useRef({ scope: `${customerId}/${kind}/${reconciliationId}`, id: selectedId });
  useEffect(() => {
    const scope = `${customerId}/${kind}/${reconciliationId}`;
    const previous = previousSelection.current;
    previousSelection.current = { scope, id: selectedId };
    if (previous.scope !== scope && selectedId && String(previous.id) === String(selectedId)) onSelect(null);
  }, [customerId, kind, reconciliationId, selectedId, onSelect]);
  const cursor = cursors[cursors.length - 1];
  const label = kind === 'rto' ? 'Lease agreement' : 'Transaction';

  useEffect(() => { setSearch(''); setCursors([null]); setSelected(null); }, [customerId, kind, reconciliationId]);
  useEffect(() => {
    const request = ++sequence.current;
    setError(null);
    if (!customerId) { setPage({ data: [], next_cursor: null }); setLoading(false); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const query = new URLSearchParams({ kind, limit: '25', ...(search ? { search } : {}), ...(cursor ? { cursor } : {}) });
        const base = reconciliationId ? `/reconciliation/${reconciliationId}/candidates` : `/customers/${customerId}/payables`;
        if (reconciliationId) query.set('customer_id', customerId);
        const result = await apiCall(`${API_BASE_URL}${base}?${query}`);
        if (result?.error) throw new Error(result.error);
        if (sequence.current === request) setPage(result);
      } catch (err) { if (sequence.current === request) setError(err.message || 'Could not load payable items'); }
      finally { if (sequence.current === request) setLoading(false); }
    }, search ? 250 : 0);
    return () => { clearTimeout(timer); sequence.current++; };
  }, [apiCall, customerId, kind, reconciliationId, search, cursor, retry]);

  const excluded = new Set(excludeIds.map(String));
  const picked = selected && String(selected.id) === String(selectedId) ? selected : page.data?.find(row => String(row.id) === String(selectedId));
  return <Form.Group className="mb-3">
    <Form.Label>{label}{required ? ' *' : ''}</Form.Label>
    <Form.Control aria-label={`Search ${label.toLowerCase()}`} placeholder={customerId ? `Search ${label.toLowerCase()} by number, vehicle or date` : 'Select a customer first'}
      disabled={!customerId} value={search} onChange={e => { setSearch(e.target.value); setCursors([null]); }} />
    {selectedId && <div className="small mt-1">Selected: {picked?.label || `#${selectedId}`} <Button variant="link" size="sm" onClick={() => { setSelected(null); onSelect(null); }}>Clear</Button></div>}
    {error && <Alert variant="danger" className="mt-2">{error} <Button size="sm" onClick={() => setRetry(n => n + 1)}>Retry</Button></Alert>}
    {loading ? <div className="mt-2"><Spinner animation="border" size="sm" /> Loading payable items…</div> : customerId && !error && <>
      <div className="list-group mt-2" role="listbox" aria-label={`${label} candidates`}>
        {(page.data || []).filter(row => !excluded.has(String(row.id))).map(row => <Button type="button" key={row.id}
          variant={String(row.id) === String(selectedId) ? 'primary' : 'outline-secondary'} className="text-start w-100" role="option"
          style={{ whiteSpace: 'normal', textOverflow: 'clip', maxWidth: '100%', height: 'auto', minHeight: '2.75rem', overflowWrap: 'anywhere' }}
          aria-selected={String(row.id) === String(selectedId)} onClick={() => { setSelected(row); onSelect(row); }}>
          {row.label} — {formatCents(dollarsToCents(row.remaining_balance))} remaining
        </Button>)}
      </div>
      {!page.data?.length && <Form.Text>No payable items found.</Form.Text>}
      {(cursors.length > 1 || page.next_cursor) && <div className="d-flex gap-2 mt-2">
        <Button type="button" size="sm" variant="outline-secondary" disabled={cursors.length === 1} onClick={() => setCursors(prev => prev.slice(0, -1))}>Previous items</Button>
        <Button type="button" size="sm" variant="outline-secondary" disabled={!page.next_cursor} onClick={() => setCursors(prev => [...prev, page.next_cursor])}>Next items</Button>
      </div>}
    </>}
  </Form.Group>;
}
